import { Injectable } from "@angular/core";
import { Action } from "@ngrx/store";
import { ActionWithPayload, SYNC_SUFFIX } from "@obg/ngrx-utils";
import { AppError } from "@obg/ngx.core";

import {
	Consents,
	ConsentsResponse,
	MarketingSettings,
	MarketingSettingsWithTokenRequest,
} from "./marketing-settings.model";

export const ACTION_PREFIX = "[Marketing Settings]";
export const MARKETING_SETTINGS_ACTION_TYPE = {
	getConsents: `${ACTION_PREFIX} Get Consents`,
	getConsentsSuccess: `${ACTION_PREFIX} Get Consents Success`,
	setDefaultConsents: `${ACTION_PREFIX} Set Default Consents ${SYNC_SUFFIX}`,
	getConsentsFail: `${ACTION_PREFIX} Get Consents Fail`,

	updateConsents: `${ACTION_PREFIX} Update Consents ${SYNC_SUFFIX}`,

	updateCustomerConsents: `${ACTION_PREFIX} Update Customer Consents`,
	updateCustomerConsentsSuccess: `${ACTION_PREFIX} Update Customer Consents Success`,
	updateCustomerConsentsFail: `${ACTION_PREFIX} Update Customer Consents Fail`,

	updateMarketingSettings: `${ACTION_PREFIX} Update Marketing Settings ${SYNC_SUFFIX}`,
	updateMarketingSettingsSuccess: `${ACTION_PREFIX} Update Marketing Settings Success ${SYNC_SUFFIX}`,
	updateMarketingSettingsFail: `${ACTION_PREFIX} Update Marketing Settings Fail ${SYNC_SUFFIX}`,

	updateMarketingSettingsFromLoginFlow: `${ACTION_PREFIX} Update Marketing Settings From Login Flow ${SYNC_SUFFIX}`,
	updateMarketingSettingsFromLoginFlowSuccess: `${ACTION_PREFIX} Update Marketing Settings From Login Flow Success ${SYNC_SUFFIX}`,
	updateMarketingSettingsFromLoginFlowFail: `${ACTION_PREFIX} Update Marketing Settings From Login Flow Fail ${SYNC_SUFFIX}`,

	updateMarketingSettingsWithToken: `${ACTION_PREFIX} Update Marketing Settings With Token`,
	updateMarketingSettingsWithTokenSuccess: `${ACTION_PREFIX} Update Marketing Settings With Token Success`,
	updateMarketingSettingsWithTokenFail: `${ACTION_PREFIX} Update Marketing Settings With Token Fail`,

	consentsNotAccepted: `${ACTION_PREFIX} Consents Not Accepted ${SYNC_SUFFIX}`,

	setMissingMarketingSettingsDetails: `${ACTION_PREFIX} Set Details ${SYNC_SUFFIX}`,
	clearMissingMarketingSettingsDetails: `${ACTION_PREFIX} Clear Details ${SYNC_SUFFIX}`,

	clearConsents: `${ACTION_PREFIX} Clear Consents ${SYNC_SUFFIX}`,
	clearMissingConsents: `${ACTION_PREFIX} Clear Missing Consents ${SYNC_SUFFIX}`,
};

@Injectable({
	providedIn: "root",
})
export class MarketingSettingsAction {
	getConsents(): Action {
		return {
			type: MARKETING_SETTINGS_ACTION_TYPE.getConsents,
		};
	}

	getConsentsSuccess(payload: ConsentsResponse): ActionWithPayload {
		return {
			type: MARKETING_SETTINGS_ACTION_TYPE.getConsentsSuccess,
			payload: payload.consents,
		};
	}

	getConsentsFail(payload: AppError): ActionWithPayload {
		return {
			type: MARKETING_SETTINGS_ACTION_TYPE.getConsentsFail,
			payload,
		};
	}

	updateConsents(payload: Partial<Consents>): ActionWithPayload {
		return {
			type: MARKETING_SETTINGS_ACTION_TYPE.updateConsents,
			payload,
		};
	}

	updateCustomerConsents(payload: Partial<Consents>): ActionWithPayload {
		return {
			type: MARKETING_SETTINGS_ACTION_TYPE.updateCustomerConsents,
			payload,
		};
	}

	updateCustomerConsentsSuccess(payload: Partial<Consents>): ActionWithPayload {
		return {
			type: MARKETING_SETTINGS_ACTION_TYPE.updateCustomerConsentsSuccess,
			payload,
		};
	}

	updateCustomerConsentsFail(payload: AppError): ActionWithPayload {
		return {
			type: MARKETING_SETTINGS_ACTION_TYPE.updateCustomerConsentsFail,
			payload,
		};
	}

	updateMarketingSettings(payload: MarketingSettings): ActionWithPayload {
		return {
			type: MARKETING_SETTINGS_ACTION_TYPE.updateMarketingSettings,
			payload,
		};
	}

	updateMarketingSettingsSuccess(payload: MarketingSettings): ActionWithPayload {
		return {
			type: MARKETING_SETTINGS_ACTION_TYPE.updateMarketingSettingsSuccess,
			payload,
		};
	}

	updateMarketingSettingsFail(payload: AppError): ActionWithPayload {
		return {
			type: MARKETING_SETTINGS_ACTION_TYPE.updateMarketingSettingsFail,
			payload,
		};
	}

	updateMarketingSettingsFromLoginFlow(payload: MarketingSettings): ActionWithPayload {
		return {
			type: MARKETING_SETTINGS_ACTION_TYPE.updateMarketingSettingsFromLoginFlow,
			payload,
		};
	}

	updateMarketingSettingsFromLoginFlowSuccess(payload: MarketingSettings): ActionWithPayload {
		return {
			type: MARKETING_SETTINGS_ACTION_TYPE.updateMarketingSettingsFromLoginFlowSuccess,
			payload,
		};
	}

	updateMarketingSettingsFromLoginFlowFail(payload: AppError): ActionWithPayload {
		return {
			type: MARKETING_SETTINGS_ACTION_TYPE.updateMarketingSettingsFromLoginFlowFail,
			payload,
		};
	}

	updateMarketingSettingsWithToken(payload: MarketingSettingsWithTokenRequest): ActionWithPayload {
		return {
			type: MARKETING_SETTINGS_ACTION_TYPE.updateMarketingSettingsWithToken,
			payload,
		};
	}

	updateMarketingSettingsWithTokenSuccess(payload: MarketingSettingsWithTokenRequest): ActionWithPayload {
		return {
			type: MARKETING_SETTINGS_ACTION_TYPE.updateMarketingSettingsWithTokenSuccess,
			payload,
		};
	}

	updateMarketingSettingsWithTokenFail(payload: AppError): ActionWithPayload {
		return {
			type: MARKETING_SETTINGS_ACTION_TYPE.updateMarketingSettingsWithTokenFail,
			payload,
		};
	}

	consentsNotAccepted(payload: MarketingSettings): ActionWithPayload {
		return {
			type: MARKETING_SETTINGS_ACTION_TYPE.consentsNotAccepted,
			payload,
		};
	}

	setMissingMarketingSettingsDetails(payload: MarketingSettings): ActionWithPayload {
		return {
			type: MARKETING_SETTINGS_ACTION_TYPE.setMissingMarketingSettingsDetails,
			payload,
		};
	}

	setDefaultConsents(payload: Consents): ActionWithPayload {
		return {
			type: MARKETING_SETTINGS_ACTION_TYPE.setDefaultConsents,
			payload,
		};
	}

	clearMissingMarketingSettingsDetails(): Action {
		return {
			type: MARKETING_SETTINGS_ACTION_TYPE.clearMissingMarketingSettingsDetails,
		};
	}

	clearConsents(): Action {
		return {
			type: MARKETING_SETTINGS_ACTION_TYPE.clearConsents,
		};
	}

	clearMissingConsents(): Action {
		return {
			type: MARKETING_SETTINGS_ACTION_TYPE.clearMissingConsents,
		};
	}
}
